/**
 * Sequential paging for callers that hold an RTK Query lazy-query trigger.
 *
 * `fetchAllPages` (the default export of ./fetchAllPages) serves endpoints that
 * merge their pages inside a `queryFn`; this module is for components that want
 * every row of a list they already request page by page.
 */

/**
 * The most rows LiftCloud serves for one page (`maxPageLimit` in its
 * `utils/pagination.go`). A larger `limit` is clamped to this rather than
 * honoured, so a client that wants more rows than this has to page for them.
 */
export const SERVER_MAX_PAGE_LIMIT = 500;

/**
 * One page of a paginated list as an RTK Query trigger resolves it: the rows
 * and, when the response carried a meta block, the row count across all pages.
 */
export interface PageResult<T> {
  data?: T[] | null;
  total?: number | bigint | null;
}

/**
 * Collects every row of a paginated list by requesting page 1, 2, 3 and so on
 * until the list runs out, for callers that hold a lazy query trigger rather
 * than a `queryFn`.
 *
 * The walk ends at the first of: the rows collected so far reaching `total`
 * (when the response has one), a page shorter than `pageSize`, an empty page,
 * or `maxPages` pages requested. Pages are requested one at a time because
 * whether page N + 1 exists is only known once page N has come back, and a
 * failed page rejects the whole call rather than returning a silently
 * truncated list.
 *
 * `pageSize` defaults to the server ceiling: asking for more only gets the
 * ceiling back, and a short page is how the walk would then end early.
 */
export async function fetchEveryPage<T>(
  fetchPage: (page: number, limit: number) => Promise<PageResult<T> | undefined>,
  pageSize: number = SERVER_MAX_PAGE_LIMIT,
  maxPages: number = 100,
): Promise<T[]> {
  const rows: T[] = [];

  const collectFrom = async (page: number): Promise<T[]> => {
    const result = await fetchPage(page, pageSize);
    const pageRows = result?.data ?? [];
    rows.push(...pageRows);

    // The paginated decoder reports a total of 0 for a response with no meta
    // block, so 0 next to rows is "unknown", not "none left".
    const total = Number(result?.total);
    const reachedTotal = Number.isFinite(total) && total > 0 && rows.length >= total;
    const lastPage = pageRows.length === 0 || pageRows.length < pageSize;
    if (reachedTotal || lastPage || page >= maxPages) {
      return rows;
    }
    return collectFrom(page + 1);
  };

  return collectFrom(1);
}
